
import { Rental, Bike, Courier } from '@/services/types';
import { mockRentals, mockBikes, mockCouriers } from '@/services/mockData';
import { fetchTableData } from '@/integrations/supabase/tableUtils';
import { 
  fetchRentalsWithClient,
  fetchRentalsWithRest
} from './services/rentalFetchApi';
import { mapRentalsData } from './services/rentalMapper';
import logger from '@/utils/logger';

// Типы для результата загрузки данных
export interface RentalDataResult {
  rentals: Rental[];
  bikes: Bike[];
  couriers: Courier[];
  dataSource: 'supabase' | 'mock';
  error: string | null;
}

export const loadRentalsData = async (): Promise<RentalDataResult> => {
  let rentalsData = null;
  let bikesData = null;
  let couriersData = null;
  let error = null;
  
  // Пробуем загрузить данные через клиент Supabase с JOIN запросом
  const clientResult = await fetchRentalsWithClient();
  
  if (!clientResult.error && clientResult.data && clientResult.data.length > 0) {
    rentalsData = clientResult.data;
    logger.info('Успешно загружены данные через клиент Supabase с JOIN:', rentalsData.length);
    // Логируем пример данных для проверки структуры
    if (rentalsData.length > 0) {
      logger.debug('Пример данных аренды с JOIN:', {
        id: rentalsData[0].id,
        status: rentalsData[0].status,
        courier: rentalsData[0].couriers,
        bike: rentalsData[0].bikes,
        courier_id: rentalsData[0].courier_id,
        bike_id: rentalsData[0].bike_id
      });
    }
  } else {
    logger.debug('Не удалось загрузить данные через клиент Supabase:', clientResult.error);
    // Пробуем загрузить через REST API с JOIN
    const restResult = await fetchRentalsWithRest();
    
    if (!restResult.error && restResult.data && restResult.data.length > 0) {
      rentalsData = restResult.data;
      logger.info('Успешно загружены данные через REST API с JOIN:', rentalsData.length);
      // Логируем пример данных для проверки структуры
      if (rentalsData.length > 0) {
        logger.debug('Пример данных аренды с JOIN:', {
          id: rentalsData[0].id,
          status: rentalsData[0].status,
          courier: rentalsData[0].couriers,
          bike: rentalsData[0].bikes,
          courier_id: rentalsData[0].courier_id,
          bike_id: rentalsData[0].bike_id
        });
      }
    } else {
      logger.debug('Не удалось загрузить данные через REST API:', restResult.error);
      // Fallback на старый метод без JOIN
      const tableResult = await fetchTableData('rentals');
      
      if (tableResult.success && tableResult.data && tableResult.data.length > 0) {
        rentalsData = tableResult.data;
        logger.info('Успешно загружены данные через fetchTableData (без JOIN):', rentalsData.length);
      } else {
        error = tableResult.message || 'Не удалось загрузить данные аренд из Supabase';
        logger.error('Все методы загрузки данных аренд завершились с ошибкой:', error);
      }
    }
  }
  
  // Если JOIN не работает или данные отсутствуют, загружаем данные отдельно
  if (rentalsData && (!rentalsData[0]?.couriers || !rentalsData[0]?.bikes)) {
    logger.debug('JOIN данные отсутствуют, загружаем велосипеды и курьеров отдельно...');
    
    // Загружаем данные велосипедов
    const bikesResult = await fetchTableData('bikes');
    if (bikesResult.success && bikesResult.data) {
      bikesData = bikesResult.data;
      logger.info('Загружено велосипедов отдельно:', bikesData.length);
    }
    
    // Загружаем данные курьеров
    const couriersResult = await fetchTableData('couriers');
    if (couriersResult.success && couriersResult.data) {
      couriersData = couriersResult.data;
      logger.info('Загружено курьеров отдельно:', couriersData.length);
    }
  }
  
  // Маппинг данных с учетом отдельно загруженных данных
  const formattedRentals = rentalsData 
    ? mapRentalsData(rentalsData, bikesData, couriersData)
    : mockRentals;
  
  // Извлекаем уникальные данные велосипедов из загруженных аренд или используем отдельно загруженные
  const bikesFromRentals = rentalsData 
    ? rentalsData
        .filter(rental => rental.bikes)
        .map(rental => rental.bikes)
        .filter((bike, index, self) => bike && self.findIndex(b => b && b.id === bike.id) === index)
    : [];
  
  const formattedBikes: Bike[] = bikesData && bikesData.length > 0
    ? bikesData.map((bike: any) => ({
        id: bike.id,
        serialNumber: bike.serial_number || 'СН-ХХХХ',
        model: bike.model || 'Неизвестная модель',
        status: bike.status || 'available' as const,
        mileage: bike.mileage || 0,
        lastMaintenance: bike.last_maintenance || new Date().toISOString()
      }))
    : bikesFromRentals.length > 0
    ? bikesFromRentals.map((bike: any) => ({
        id: bike.id,
        serialNumber: bike.serial_number || 'СН-ХХХХ',
        model: bike.model || 'Неизвестная модель',
        status: 'available' as const,
        mileage: 0,
        lastMaintenance: new Date().toISOString()
      }))
    : mockBikes;
  
  // Извлекаем уникальные данные курьеров из загруженных аренд или используем отдельно загруженные
  const couriersFromRentals = rentalsData 
    ? rentalsData
        .filter(rental => rental.couriers)
        .map(rental => rental.couriers)
        .filter((courier, index, self) => courier && self.findIndex(c => c && c.id === courier.id) === index)
    : [];
  
  const formattedCouriers: Courier[] = couriersData && couriersData.length > 0
    ? couriersData.map((courier: any) => ({
        id: courier.id,
        name: courier.name || 'Неизвестный курьер',
        status: courier.status || 'active' as const,
        email: courier.email || '',
        phone: courier.phone || ''
      }))
    : couriersFromRentals.length > 0
    ? couriersFromRentals.map((courier: any) => ({
        id: courier.id,
        name: courier.name || 'Неизвестный курьер',
        status: 'active' as const,
        email: courier.email || '',
        phone: courier.phone || ''
      }))
    : mockCouriers;
  
  logger.info(`Обработано ${formattedRentals.length} аренд, ${formattedBikes.length} велосипедов, ${formattedCouriers.length} курьеров`);
  
  return {
    rentals: formattedRentals,
    bikes: formattedBikes,
    couriers: formattedCouriers,
    dataSource: rentalsData ? 'supabase' : 'mock',
    error
  };
};
